import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWrench, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'

import { MachineActions } from "redux/actions/settings/MachineActions";
import { MachineHistoryActions } from "redux/actions/machines/MachineHistoryActions";
import { MachineDefectActions } from "redux/actions/machines/MachineDefectActions";
import { TableColumn, Table, TableRow, sortType } from "app/components/common/Table";
import { PageContentHeader } from "app/components/common/PageContentHeader";
import Translator from "app/components/common/Translator";
import { Translate } from "infrastructure/mapping/Translate";

import { StoreModel } from "models/StoreModel";
import { LoginUser } from "models/LoginUser";
import { Machine } from "models/Machine";
import { MachineHistory } from "models/MachineHistory";
import { Translations } from "models/Translations";


interface Props {
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    deleteMachineDefect: typeof MachineDefectActions.deleteMachineDefect;
    moveToEditDailyReport: typeof MachineHistoryActions.moveToEditDailyReport;
    moveToAddDailyReport: typeof MachineHistoryActions.moveToAddDailyReport;
    loginUser: LoginUser;
    machines: Machine[];
    machineHistories: MachineHistory[];
    translations: Translations;
}

interface State {
    selectedOption?: SelectedOption | null; // undefined until the user picks, null shows all machines
}

type SelectedOption = {
    label: string,
    value: number
}

class MachineDefects extends React.Component<Props, State> {
    public readonly state: State = {}

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machines && this.props.machineHistories
    }

    getOptions(): SelectedOption[] {
        return this.props.machines
            .filter(m => !m.archivedDate)
            .map(m => ({ label: m.name, value: m.id }));
    }

    selectedOption() {
        if (this.state.selectedOption !== undefined)
            return this.state.selectedOption;
        const assigned = Machine.assignedTo(this.props.machines, this.props.loginUser.userData?.id);
        return this.getOptions().find(o => o.value === assigned?.id) ?? null;
    }

    isFixed(defectId: number) {
        return this.props.machineHistories.some(mh => mh.fixedMachineDefects?.some(fmd => fmd.id === defectId));
    }

    // a defect is created in the history entry that lists it in machineDefects
    setDefectData(): TableRow[] {
        const machineId = this.selectedOption()?.value;
        let data: TableRow[] = [];
        this.props.machineHistories
            .filter(mh => !machineId || mh.machine.id === machineId)
            .forEach(mh => mh.machineDefects?.filter(md => md.id).forEach(md => {
                const fixed = this.isFixed(md.id);
                data.push({
                    dataObject: {
                        id: md.id,
                        machine: mh.machine,
                        date: mh.date,
                        serviceLife: mh.serviceLife,
                        defect: md.defect,
                        description: md.description,
                        outOfOrder: md.outOfOrderFlag,
                        fixed: fixed,
                        archivedDate: md.archivedDate ?? mh.archivedDate
                    },
                    cells: [
                        {
                            name: "serviceLife",
                            sortVal: mh.serviceLife,
                            cell: <>{mh.machine.machineType.serviceLifeUnit?.short ? mh.serviceLife + " " + mh.machine.machineType.serviceLifeUnit.short : mh.serviceLife}</>,
                        },
                        {
                            name: "outOfOrder",
                            sortVal: md.outOfOrderFlag ? 1 : 0,
                            cell: md.outOfOrderFlag ? <FontAwesomeIcon icon={faTriangleExclamation} className="c-table__cell--icon c-table__cell--icon-error" title={Translate.text(this.props.translations, "Out of order")}></FontAwesomeIcon> : <></>,
                        },
                        {
                            name: "fixed",
                            sortVal: fixed ? 1 : 0,
                            cell: fixed ? <FontAwesomeIcon icon={faWrench} className="c-table__cell--icon c-table__cell--icon-success"></FontAwesomeIcon> : <></>,
                        },
                    ]
                })
            }));
        return data;
    }

    // defects are edited in the daily report they were reported in
    editMachineDefect(id: number) {
        const mh = this.props.machineHistories.find(mh => mh.machineDefects?.some(md => md.id === id));
        if (mh)
            this.props.moveToEditDailyReport(mh.id);
    }

    setColumns(): TableColumn[] {
        return [
            { header: 'Id', accessor: 'id' },
            { header: 'Machine', accessor: 'machine' },
            { header: 'Date', accessor: 'date' },
            { header: 'Service Life', accessor: 'serviceLife' },
            { header: 'Defect', accessor: 'defect' },
            { header: 'Description', accessor: 'description' },
            { header: 'Out of order', accessor: 'outOfOrder' },
            { header: 'Fixed', accessor: 'fixed' },
        ];
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Machine Defects" />
                    <Translator text="Loading" />
                </div>
            )
        }
        return (
            <div className="c-page__content">
                <PageContentHeader text="Machine Defects" />
                <div className="c-card">
                    <Select
                        value={this.selectedOption()}
                        onChange={selectableOption => this.setState({ selectedOption: selectableOption })}
                        options={this.getOptions()}
                        isClearable
                        placeholder={Translate.text(this.props.translations, "Machine")}
                        className={"c-form__select"}
                        classNamePrefix="c-select"
                        // render the menu outside the card, which would clip it
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 10 }) }}
                    />
                </div>
                <div className="c-card">
                    <Table
                        editable={true}
                        editAction={this.editMachineDefect.bind(this)}
                        addAction={this.props.moveToAddDailyReport}
                        deleteAction={this.props.deleteMachineDefect}
                        columns={this.setColumns()}
                        defaultSort={{ columnId: 'date', sortType: sortType.desc }}
                        tableData={this.setDefectData()}
                        translations={this.props.translations}
                        loginUser={this.props.loginUser}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineHistories: state.machineHistories,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
        deleteMachineDefect: bindActionCreators(MachineDefectActions.deleteMachineDefect, dispatch),
        moveToEditDailyReport: bindActionCreators(MachineHistoryActions.moveToEditDailyReport, dispatch),
        moveToAddDailyReport: bindActionCreators(MachineHistoryActions.moveToAddDailyReport, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MachineDefects);
